<template>
  <div>
    <AccountTable
      v-if="unsavedRows.length"
      :rows="unsavedRows"
      :title="unsavedTitle"
    />
    <AccountTable
      v-if="savedRows.length"
      :rows="savedRows"
      :title="savedTitle"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AccountTable from './account-table.vue'
import { useCommon } from '../store/common'
import type { AccountTableRowStatus } from '../constants'
import { accountTableRowStatuses } from '../constants'
import type { Account, AccountTableRow, Group } from '../shared-types'

interface Props {
  mappingListAccountTypes: Array<string>
}

const props = defineProps<Props>()

const commonStore = useCommon()
const unsavedTitle = window.I18n.t('account_mapping_list.table_titles.unsaved')
const savedTitle = window.I18n.t('account_mapping_list.table_titles.saved')

const determineIconStatus = (
  account: Account,
  isLowConfidence: boolean,
  isSaved: boolean
): AccountTableRowStatus => {
  if (isSaved) {
    return accountTableRowStatuses.NONE
  }

  if (account.remappedByUser) {
    return accountTableRowStatuses.EDITED
  }

  if (isLowConfidence) {
    return accountTableRowStatuses.UNCERTAIN
  }

  return accountTableRowStatuses.NONE
}

const rows = computed(() => {
  return commonStore.accountIds.map((accountId) => {
    const account = commonStore.accounts[accountId]
    const mappedData = commonStore.getFlatMappedAccounts[accountId]

    const groupId = mappedData?.groupId
    const mappedAccount = groupId ? commonStore.groups[groupId] : ({} as Group)

    const balance = commonStore.accountBalances[accountId]
    const sortableBalance = balance ? parseFloat(balance.value) : NaN

    const mappedAccountNumber = mappedAccount.accountRange || 0
    const recommendation = commonStore.recommendations[account.id] || {
      proposals: [],
      low_confidence: false,
    }

    const isLowConfidence =
      recommendation.proposals.length > 0 && recommendation.low_confidence

    const status = determineIconStatus(
      account,
      isLowConfidence,
      mappedData?.isSaved
    )

    return {
      ...account,
      mappedAccount: {
        ...mappedAccount,
        ...mappedData,
      },
      mappedAccountNumber, // for SfTable sorting
      mappedAccountType: mappedAccount.type || '', // for SfTable sorting
      balance,
      sortableBalance,
      recommendation,
      status,
      isLowConfidence,
      accountTypeExistsInMappingList: props.mappingListAccountTypes.includes(
        account.accountType || ''
      ),
    } as AccountTableRow
  }) as Array<AccountTableRow>
})

const unsavedRows = computed(() =>
  rows.value.filter((row) => !row.mappedAccount.isSaved)
)
const savedRows = computed(() =>
  rows.value.filter((row) => row.mappedAccount.isSaved)
)
</script>
